import React, { useMemo } from "react";
import { AreaChart } from "@/components/ui/charts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { cn } from "@/lib/utils";
import { useReportingData } from "@/hooks/useReportingData";

export interface RevenueChartProps {
  className?: string;
  detailed?: boolean;
  dateRange?: { from: Date; to: Date };
}

function getTotalRevenue(data: any[]) {
  return data.reduce((sum, d) => sum + (d.revenue || 0), 0);
}

function getPercentChange(data: any[]) {
  if (data.length < 2) return 0;
  const last = data[data.length - 1]?.revenue || 0;
  const prev = data[data.length - 2]?.revenue || 0;
  if (prev === 0) return last > 0 ? 100 : 0;
  return ((last - prev) / prev) * 100;
}

function downloadCSV(data: any[]) {
  const header = "Month,Revenue";
  const rows = data.map(d => `${d.month},${d.revenue}`);
  const csv = [header, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `revenue-report.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function RevenueChart({ className, detailed = false, dateRange }: RevenueChartProps) {
  const { revenueData } = useReportingData(dateRange);

  const totalRevenue = useMemo(() => getTotalRevenue(revenueData), [revenueData]);
  const percentChange = useMemo(() => getPercentChange(revenueData), [revenueData]);

  // Format currency values
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Card className={cn("shadow-lg border-0 bg-gradient-to-br from-white via-blue-50 to-blue-100/60 backdrop-blur-md p-0", className)}>
      <CardHeader className="pb-2 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <CardTitle className="text-2xl font-bold text-blue-900 flex items-center gap-2">
            Revenue
            <span className="text-lg font-normal text-blue-500">(USD)</span>
          </CardTitle>
          <div className="flex items-baseline gap-4 mt-2">
            <span className="text-3xl font-extrabold text-blue-700">{formatCurrency(totalRevenue)}</span>
            <span className={cn(
              "text-sm font-semibold px-2 py-1 rounded-full",
              percentChange > 0 ? "bg-green-100 text-green-700" : percentChange < 0 ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-700"
            )}>
              {percentChange > 0 ? "+" : ""}{percentChange.toFixed(1)}%
            </span>
            <span className="text-xs text-muted-foreground">vs previous period</span>
          </div>
        </div>
        <Button
          variant="outline"
          className="flex items-center gap-2 text-blue-700 border-blue-200 hover:bg-blue-50"
          onClick={() => downloadCSV(revenueData)}
        >
          <Download className="h-4 w-4" /> Download CSV
        </Button>
      </CardHeader>
      <CardContent className="pt-0 pb-6">
        <div className="h-80 w-full">
          <AreaChart
            data={revenueData}
            index="month"
            categories={["revenue"]}
            colors={["#2563eb"]}
            valueFormatter={formatCurrency}
          />
        </div>
      </CardContent>
    </Card>
  );
}
